import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["cell", "filter", "table", "hideClosed", "drawer"]
  static values = { home: String, editable: { type: Boolean, default: true } }

  connect() {
    this.activeIndex = 0
    this.highlight()
    this.boundKeydown = this.keydown.bind(this)
    this.boundFrameMissing = this.frameMissing.bind(this)
    this.boundFrameLoad = this.drawerLoaded.bind(this)
    this.boundStreamRender = this.streamRender.bind(this)
    this.element.addEventListener("keydown", this.boundKeydown)
    this.element.addEventListener("turbo:frame-missing", this.boundFrameMissing)
    this.element.addEventListener("turbo:frame-load", this.boundFrameLoad)
    document.addEventListener("turbo:before-stream-render", this.boundStreamRender)
    this.restoreHideClosed()
    if (this.drawerOpen()) this.focusDrawer()
  }

  disconnect() {
    this.element.removeEventListener("keydown", this.boundKeydown)
    this.element.removeEventListener("turbo:frame-missing", this.boundFrameMissing)
    this.element.removeEventListener("turbo:frame-load", this.boundFrameLoad)
    document.removeEventListener("turbo:before-stream-render", this.boundStreamRender)
  }

  // A live update is rendered once for every subscriber, so it carries a
  // writer's edit links. A read-only board strips them after each render;
  // the server refuses the write either way.
  streamRender(event) {
    if (this.editableValue) return
    const render = event.detail.render
    event.detail.render = async (streamElement) => {
      await render(streamElement)
      this.stripEditing()
    }
  }

  stripEditing() {
    this.element.querySelectorAll("a.issue-cell-edit").forEach((link) => {
      link.closest(".issue-cell-value")?.classList.add("is-readonly")
      link.remove()
    })
    this.element.querySelectorAll("form.issue-cell-form, .issue-cell-actions").forEach((node) => node.remove())
    this.element.querySelectorAll(".issue-drawer input, .issue-drawer select, .issue-drawer textarea, .issue-drawer button[type=submit]")
      .forEach((field) => { field.disabled = true })
  }

  keydown(event) {
    if (event.key === "/" && !this.inEditor() && !this.inFilter(event)) {
      event.preventDefault()
      if (this.hasFilterTarget) this.filterTarget.focus()
      return
    }

    if (event.key === "Escape" && this.drawerOpen() && !this.inEditor()) {
      event.preventDefault()
      this.closeDrawer()
      return
    }

    if (this.inFilter(event) || this.inEditor()) {
      if (event.key === "Escape" && this.inEditor()) {
        this.element.querySelector(".issue-cell-form .issue-cell-cancel")?.click()
      }
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter" && this.inEditor()) {
        event.preventDefault()
        this.element.querySelector(".issue-cell-form")?.requestSubmit()
      }
      if (event.key === "Tab" && this.inEditor()) {
        event.preventDefault()
        const form = this.element.querySelector(".issue-cell-form")
        if (form) {
          form.addEventListener("turbo:submit-end", () => this.move(1), { once: true })
          form.requestSubmit()
        }
      }
      return
    }

    if (event.key === "ArrowRight") { event.preventDefault(); this.move(1) }
    if (event.key === "ArrowLeft") { event.preventDefault(); this.move(-1) }
    if (event.key === "ArrowDown") { event.preventDefault(); this.moveRow(1) }
    if (event.key === "ArrowUp") { event.preventDefault(); this.moveRow(-1) }
    if (event.key === "Enter") {
      event.preventDefault()
      this.activeCell()?.querySelector("a")?.click()
    }
  }

  move(delta) {
    const cells = this.cellTargets
    if (cells.length === 0) return
    this.activeIndex = (this.activeIndex + delta + cells.length) % cells.length
    this.highlight()
  }

  moveRow(delta) {
    const cells = this.cellTargets
    if (cells.length === 0) return
    const columns = this.columnCount()
    this.activeIndex = Math.min(cells.length - 1, Math.max(0, this.activeIndex + (delta * columns)))
    this.highlight()
  }

  columnCount() {
    const firstRow = this.element.querySelector(".issue-row")
    return firstRow ? firstRow.querySelectorAll("[data-issue-grid-target='cell']").length : 1
  }

  highlight() {
    this.cellTargets.forEach((cell, index) => {
      cell.classList.toggle("is-active", index === this.activeIndex)
    })
    this.activeCell()?.scrollIntoView({ block: "nearest", inline: "nearest" })
  }

  activeCell() {
    return this.cellTargets[this.activeIndex]
  }

  inEditor() {
    return Boolean(this.element.querySelector(".issue-cell-form"))
  }

  cancelEdit(event) {
    event.preventDefault()
    this.reloadCell(event.currentTarget.closest("turbo-frame"))
  }

  frameMissing(event) {
    if (!event.target.classList?.contains("issue-cell")) return

    event.preventDefault()
    this.reloadCell(event.target)
  }

  reloadCell(frame) {
    const url = frame?.dataset?.showUrl
    if (!url) return

    frame.src = url
  }

  inFilter(event) {
    return this.hasFilterTarget && this.filterTarget.contains(event.target)
  }

  applyFilters(event) {
    event.target.form?.requestSubmit()
  }

  toggleHideClosed(event) {
    const checked = event.target.checked
    localStorage.setItem("ablefy-issues-hide-closed", checked ? "1" : "0")
    event.target.form?.requestSubmit()
  }

  restoreHideClosed() {
    if (!this.hasHideClosedTarget) return
    const stored = localStorage.getItem("ablefy-issues-hide-closed")
    if (stored === "1" && !this.hideClosedTarget.checked) {
      this.hideClosedTarget.checked = true
      this.hideClosedTarget.form?.requestSubmit()
    }
  }

  openDrawer(event) {
    if (event.target.closest("a.issue-cell-edit, a.sheet-link, button, select, input, textarea, .issue-cell-form, .issue-cell.is-editing")) return
    if (event.target.closest("a[data-turbo-frame='issue_drawer']")) return

    const link = event.currentTarget.querySelector("a[data-turbo-frame='issue_drawer']")
    link?.click()
  }

  drawerLoaded(event) {
    if (event.target.id !== "issue_drawer") return
    event.target.classList.add("is-open")
    this.focusDrawer()
  }

  focusDrawer() {
    this.drawerTarget?.querySelector(".issue-drawer-close, .issue-cell-edit, button, a")?.focus()
  }

  closeDrawer() {
    if (!this.hasDrawerTarget) return
    const ref = this.drawerTarget.dataset.ref
    this.drawerTarget.innerHTML = ""
    this.drawerTarget.classList.remove("is-open")
    if (/\/issues\/ISS-/i.test(location.pathname)) {
      const form = this.element.querySelector("form.sheet-toolbar")
      history.pushState({}, "", this.homeValue || form?.action || "/issues")
    }
    if (ref) {
      this.element.querySelector(`[data-ref="${ref}"] .issue-ref-link`)?.focus()
    }
  }

  drawerOpen() {
    return this.hasDrawerTarget && this.drawerTarget.innerHTML.trim().length > 0
  }

  persistSelect(event) {
    const form = event.target.closest("form")
    if (!form) return
    form.dataset.persisting = "true"
    form.requestSubmit()
  }

  leaveSelect(event) {
    const form = event.target.closest("form")
    if (!form || form.dataset.persisting === "true") return
    form.querySelector(".issue-cell-cancel")?.click()
  }
}
